import { Box, Card, CardContent, Checkbox, Grid2, IconButton, Typography } from '@mui/material';
import { FC, useState, useEffect } from 'react';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import { BsCurrencyDollar } from 'react-icons/bs';
import { MdPrint } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { IOrdersRegisteredOnOmieEntity, OrderStatusEnum } from '../../Services/api/orders/orders.interface';
import { ConfirmDialog } from '../Dialog/variants/dialog-confirmation/dialog-confirmation.component';
import { Flex } from '../Flex/flex.component';
import { Button as ButtonCustom } from '../Form/Button/button.scomponent';
import IKanbanBoardOrdersSchemaProps from './interfaces/kanban-board-orders-schema.interface';
import { StrictModeDroppable } from './components/droppable-component';
import { OrderDetailsModal } from '../Modals/variants/order-details/order-details-modal.component';
import { IOrderData } from '../Modals/variants/order-details/order-details-modal.component';
import { ISaleEntity } from '../../Pages/Financial/Details/financial-details.hook';

export interface Task {
  id: string;
  content: {
    order: string;
    customer: string;
    carrier: string;
    observations?: string;
    os?: string; // Mantendo para compatibilidade
    type?: 'checkbox' | 'click' | 'navigate'
  };
}


interface IKanbanBoardOrdersProps {
  initialData?: IKanbanBoardOrdersSchemaProps
  handleSelectProductToBill?: (productId?:string) => void;
  handleUpdateOrderStatus: (payload: { orderId: string, newStatus: OrderStatusEnum }) => Promise<void>
  omieDetailsOrder?: IOrdersRegisteredOnOmieEntity
  handleSelectAllProductsToBill?: () => void
  billProductsOrder?: (data?: any) => Promise<any>
  canClick?: boolean;
  canDrag?: boolean
  productsToBill?: string[]
  setOmieOrderData: (state: any) => void
  onPrintPickingList?: () => void
  onCardClick?: (orderId: string) => void;
  orderDetailsData?: {
    sale?: ISaleEntity;
    orderData?: IOrderData;
  };
  onSelectSeller?: () => void;
  onOrderWithoutSeller?: () => void;
}

const KanbanBoardOrders : FC<IKanbanBoardOrdersProps> = ({ initialData, handleSelectProductToBill,  billProductsOrder, canClick, canDrag, productsToBill,  omieDetailsOrder, setOmieOrderData, handleSelectAllProductsToBill, handleUpdateOrderStatus, onPrintPickingList, onCardClick, orderDetailsData, onSelectSeller, onOrderWithoutSeller }) => {
  const [data, setData] = useState(initialData);
  
  // Atualizar dados quando initialData mudar
  useEffect(() => {
    console.log("KanbanBoardOrders - initialData received: ", initialData);
    if (initialData) {
      console.log("KanbanBoardOrders - Setting data: ", JSON.stringify(initialData, null, 2));
      setData(initialData);
    }
  }, [initialData]);
  const [tempMovement, setTempMovement] = useState<{ orderId: string, newStatus: OrderStatusEnum } | undefined>(undefined)
  const [showConfirmation, setShowConfirmation] = useState({
    show: false,
    title: '',
    description: ''
  })

  const navigate = useNavigate()

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [orderDetailsModalOpen, setOrderDetailsModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const onCancelDragAction = () => {
    setShowConfirmation({ show: false, title: '', description: ''})
    setTempMovement(undefined)
  }

  const handleCardClick = (orderId: string) => {
    console.log('Card clicked with orderId:', orderId);
    setSelectedOrderId(orderId);
    setOrderDetailsModalOpen(true);
    if (onCardClick) {
      console.log('Calling onCardClick with orderId:', orderId);
      onCardClick(orderId);
    } else {
      console.log('onCardClick is not defined');
    }
  }

  const handleCloseOrderDetailsModal = () => {
    setOrderDetailsModalOpen(false);
    setSelectedOrderId(null);
  }

  const updateLocalKanbanState = (orderId: string, newStatus: OrderStatusEnum) => {
    if (!data) return;

    setData(prevData => {
      if (!prevData) return prevData;

      const newData = { ...prevData };
      
      // Remove o task de todas as colunas
      Object.keys(newData.columns).forEach(columnId => {
        const column = newData.columns[columnId as OrderStatusEnum];
        if (column) {
          column.taskIds = column.taskIds.filter(taskId => taskId !== orderId);
        }
      });

      // Adiciona o task na nova coluna
      const targetColumn = newData.columns[newStatus];
      if (targetColumn) {
        targetColumn.taskIds.push(orderId);
      }

      return newData;
    });
  }

  const handleSubmit = async () => {
    try {
      if (!tempMovement) return;
      
      // Executa a atualização do status
      await handleUpdateOrderStatus(tempMovement)
      
      // Atualiza o estado local do Kanban apenas se a operação foi bem-sucedida
      updateLocalKanbanState(tempMovement.orderId, tempMovement.newStatus)
      
    } catch (error) {
      console.log("Erro ao atualizar status do pedido: ", error)
      // Em caso de erro, o card permanece na posição original
    } finally {
      onCancelDragAction()
    }
  }

  const onDragEnd = (result: any) => {
    console.log("drag called: ", data)
    const { destination, source, draggableId } = result;
  
    if (!destination) return;
  
    const task = data?.tasks[draggableId];
    const endColumn = data?.columns[destination.droppableId as OrderStatusEnum];
    console.log("endColumn: ", endColumn)
  
    if (task?.isLocked) {
      alert('Este card está bloqueado e não pode ser movido.');
      return;
    }

    const payload = {
      orderId: draggableId,
      newStatus: endColumn?.id as OrderStatusEnum
    }

    setTempMovement(payload)
    setShowConfirmation({
      show: true,
      title: 'Você tem certeza?',
      description: `Movimentar o pedido para "${endColumn?.title}"`
    })
  
    
  };


  // Se não há dados, mostrar mensagem
  if (!data || !data.columnOrder || data.columnOrder.length === 0) {
    return (
      <Box sx={{ padding: '20px', textAlign: 'center' }}>
        <Typography variant="h6">Nenhum pedido encontrado para exibir no kanban.</Typography>
      </Box>
    );
  }

  return (
    <>
     <ConfirmDialog open={showConfirmation?.show}  title={showConfirmation?.title} description={showConfirmation?.description} onCancel={onCancelDragAction} onConfirm={handleSubmit} />

     {/* {omieDetailsOrder?.id && <OmieOrderDetailsModal data={omieDetailsOrder} onClose={() => setOmieOrderData(undefined)}  /> } */}

    {/* <Flex sx={{ backgroundColor: '#f5f5f5', padding: '20px', justifyContent: 'flex-end' }}>
    <ButtonCustom LeftIcon={() => <BsCurrencyDollar size={18} />} sx={{ width: '280px', height: '60px'}} boxLeftSx={{ gap: '10px', marginLeft: '5px'}} textSx={{ textTransform: 'Capitalize', fontWeight: 500}} onClick={() => setShowConfirmation({
      show: true,
      title: 'Você tem certeza?',
      description: 'Essa ação irá faturar os itens selecionados'
    })} variant='outlined'>CRIAR PEDIDO E FATURAR</ButtonCustom>
    </Flex> */}
     <DragDropContext onDragEnd={onDragEnd}>
       
            <Grid2 container spacing={2}>
            {data?.columnOrder?.map((columnId) => {
                const column = data.columns[columnId];
                const tasks = column?.taskIds?.map((taskId) => data.tasks[taskId]) || [];

                if (!column) return null;

                return (
                
                    <Grid2 size={4} key={column.id}>
                      <Flex alignItems='center' justifyContent='space-between' mb='20px' width='100%'>
                        {column.checkAll && handleSelectAllProductsToBill ? <Checkbox sx={{ padding: 0, marginRight: '10px', marginLeft: '30px'}} onChange={handleSelectAllProductsToBill} checked={!!(productsToBill?.length === Object.keys(data.tasks || {}).length)}  /> : <Box />}
                        <Flex alignItems='center' gap='10px'>
                          <Typography variant="h6" align="center" >
                              {column.title}
                          </Typography>
                          {column.id === OrderStatusEnum.IN_PICKING && onPrintPickingList && (
                            <IconButton 
                              onClick={onPrintPickingList}
                              size="small"
                              sx={{ 
                                color: '#666',
                                '&:hover': { 
                                  color: '#333',
                                  backgroundColor: 'rgba(0,0,0,0.04)' 
                                }
                              }}
                              title="Imprimir lista de separação"
                            >
                              <MdPrint size={20} />
                            </IconButton>
                          )}
                        </Flex>
                        <Box />
                      </Flex>
                    <StrictModeDroppable droppableId={column.id}>
                        {(provided) => (
                        <Box
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            sx={{
                            background: '#2A2A2A',
                            padding: 2,
                            borderRadius: 1,
                            minHeight: 700,
                            }}
                        >
                            {tasks.map((task, index) => (
                              <Draggable
                                key={task.id}
                                draggableId={task.id}
                                index={index}
                                isDragDisabled={task.isLocked || !canDrag} // Bloqueia o drag
                              >
                                {(provided) => (
                                  <Card
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...(task.isLocked ? {} : provided.dragHandleProps)} // Remove o handle para itens bloqueados
                                    sx={{
                                      marginBottom: 2,
                                      opacity: task.isLocked ? 0.5 : 1, // Visualmente indicar bloqueio
                                      cursor: task.isLocked ? 'not-allowed' : 'pointer',
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!task.isLocked) {
                                        handleCardClick(task.id);
                                      }
                                    }}
                                  >
                                    {task.content.type === 'checkbox' && (
                                      <CardContent>
                                      <Flex paddingLeft='0px' flexDirection='column' alignItems='flex-start'>
                                        <Flex alignItems='center' width='100%' mb={1}>
                                          {!task.isLocked && <Checkbox 
                                          sx={{ padding: 0, marginRight: '10px'}} 
                                          onChange={() => handleSelectProductToBill && handleSelectProductToBill(task.id)} 
                                          checked={!!productsToBill?.find(i => i === task.id)} 
                                          />}
                                          
                                          <Typography 
                                          variant="body2"
                                          fontWeight="bold"
                                          onClick={() => handleCardClick(task.id)}
                                          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                          >{task?.content?.order}</Typography>
                                        </Flex>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                          {task?.content?.customer}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                          {task?.content?.carrier}
                                        </Typography>
                                        {task?.content?.observations && (
                                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                            {task?.content?.observations}
                                          </Typography>
                                        )}
                                      </Flex>
                                    </CardContent>
                                    )}

                                    {task.content.type === 'click' && (
                                      <CardContent>
                                      <Flex flexDirection='column' alignItems='flex-start' paddingLeft='0px'>
                                        <Typography 
                                          variant="body2"
                                          fontWeight="bold"
                                          onClick={() => handleCardClick(task.id)}
                                          sx={{ cursor: 'pointer', mb: 1, '&:hover': { textDecoration: 'underline' } }}
                                        >
                                          {task?.content?.order}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                          {task?.content?.customer}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                          {task?.content?.carrier}
                                        </Typography>
                                        {task?.content?.observations && (
                                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                            {task?.content?.observations}
                                          </Typography>
                                        )}
                                      </Flex>
                                    </CardContent>
                                    )}

                                    {task.content.type === 'navigate' && (
                                      <CardContent>
                                      <Flex flexDirection='column' alignItems='flex-start' paddingLeft='0px'>
                                        <Typography 
                                          variant="body2"
                                          fontWeight="bold"
                                          onClick={() => handleCardClick(task.id)}
                                          sx={{ cursor: 'pointer', mb: 1, '&:hover': { textDecoration: 'underline' } }}
                                        >
                                          {task?.content?.order}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                          {task?.content?.customer}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                          {task?.content?.carrier}
                                        </Typography>
                                        {task?.content?.observations && (
                                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                            {task?.content?.observations}
                                          </Typography>
                                        )}
                                      </Flex>
                                    </CardContent>
                                    )}
                                    
                                  </Card>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                        </Box>
                        )}
                    </StrictModeDroppable>
                    </Grid2>
                
                );
            })}
            </Grid2>
       
    </DragDropContext>

    {/* {isModalOpen && <OsDetailsModal onClose={handleCloseModal} code='1010' list={list}  /> } */}

    {/* Modal de detalhes do pedido */}
    <OrderDetailsModal
      open={orderDetailsModalOpen}
      onClose={handleCloseOrderDetailsModal}
      sale={orderDetailsData?.sale}
      orderData={orderDetailsData?.orderData}
      onSelectSeller={onSelectSeller}
      onOrderWithoutSeller={onOrderWithoutSeller}
    />
   
    </>
   
  );
};

export { KanbanBoardOrders };
